import { useState, useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { useIsMutating } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { BsEnvelope, BsPauseFill, BsPlayFill } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';
import Tabs from '#globalComponents/Tabs/Tabs';
import { useError } from '#globalContexts/ErrorContext';
import { useCapabilityCheck } from '#globalHooks/useCapabilityCheck';
import { useUrlTab } from '#globalHooks/useUrlTab';
import { useAllEmailAccounts } from '#settingsFeature/models/emailAccounts/queries';

import AdvancedSettingsTab from '../../components/advanced/AdvancedSettingsTab';
import AICampaignReviewTab from '../../components/ai-review/AICampaignReviewTab/AICampaignReviewTab';
import CampaignStatsCards from '../../components/common/CampaignStatsCards/CampaignStatsCards';
import OverviewTab from '../../components/overview/OverviewTab/OverviewTab';
import RecipientVariablesTable from '../../components/recipients/RecipientVariablesTable/RecipientVariablesTable';
import ScheduleTab from '../../components/schedule/ScheduleTab';
import StatisticsTab from '../../components/statistics/StatisticsTab';
import {
	EmailCampaignDetailsProvider,
	useEmailCampaignDetailsContext,
} from '../../contexts/EmailCampaignDetailsContext';
import { useGenerationStatus } from '../../models/aiCampaigns/queries';
import { useDeleteSingleEmailOfCampaign } from '../../models/emailCampaignMails/mutations';
import { emailCampaignsMutationKeys } from '../../models/emailCampaigns/keys';
import { useUpsertEmailListsOfCampaign } from '../../models/emailCampaigns/mutations';
import { useCampaignStats, useCampaignSends } from '../../models/emailSends/queries';

/**
 * Builds campaign save payload from current form state.
 *
 * @param {Object} params
 * @param {string} params.campaignName
 * @param {Object} params.campaign
 * @param {string} params.emailAccountId
 * @param {Object} [params.extra] - Additional fields to merge (e.g. sendingSchedule, signatureId)
 * @returns {Object}
 */
function buildSavePayload({ campaignName, campaign, emailAccountId, extra = {} }) {
	return {
		name: campaignName,
		status: campaign.campaignStatus,
		selectedListIds: campaign.emailCampaign_emailList?.map((x) => x.contactLists.id) || [],
		emailAccountId: emailAccountId || null,
		...extra,
	};
}

/**
 * Subject clause naming a list's email recipients, for the attach-to-running-campaign warning.
 * An unknown count (list not in the payload) is described without a number rather than as zero.
 *
 * @param {number|undefined} count - emailRecipientCount of the list
 * @returns {string}
 */
function describeListRecipients(count) {
	if (count === undefined || count === null) return 'Die Empfänger dieser Liste erhalten';
	if (count === 1) return 'Der Empfänger dieser Liste erhält';
	return `Die ${count} Empfänger dieser Liste erhalten`;
}

// Status Badge Component
const StatusBadge = ({ status }) => {
	const getStatusConfig = () => {
		switch (status) {
			case 'active':
				return {
					icon: BsPlayFill,
					text: 'Aktiv',
					className: 'bg-success-200 text-success-700 border-success-500/40',
				};
			case 'paused':
				return {
					icon: BsPauseFill,
					text: 'Pausiert',
					className: 'bg-caution-200 text-caution-700 border-caution-500/40',
				};
			case 'draft':
			default:
				return {
					icon: BsEnvelope,
					text: 'Entwurf',
					className: 'bg-bgBase-400 text-bgBase-800 border-bgBase-500',
				};
		}
	};

	const config = getStatusConfig();
	const Icon = config.icon;

	return (
		<div
			className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${config.className}`}
		>
			<Icon className="h-3 w-3" />
			{config.text}
		</div>
	);
};

StatusBadge.propTypes = {
	status: PropTypes.string.isRequired,
};

/**
 * Why the start button is unavailable, or an empty string when it is usable. Pausing stays
 * available without the capability so a running campaign can always be stopped.
 * @param {object} state
 * @param {boolean} state.canSend
 * @param {string} state.campaignType
 * @param {number} state.approvedEmailCount
 * @param {string|null} state.emailAccountId
 * @param {number} state.listCount
 * @param {number} state.emailCount
 * @returns {string}
 */
const buildStartBlockedReason = ({
	canSend,
	campaignType,
	approvedEmailCount,
	emailAccountId,
	listCount,
	emailCount,
}) => {
	if (!canSend) return 'Versand ist in Ihrem aktuellen Abo nicht verfügbar';
	if (!emailAccountId) return 'Bitte verknüpfen Sie zuerst ein E-Mail-Konto';
	if (campaignType === 'template' && (listCount === 0 || emailCount === 0)) {
		return 'Bitte wählen Sie mindestens eine Liste und erstellen Sie eine E-Mail';
	}
	if (campaignType === 'ai_personalized' && approvedEmailCount === 0) {
		return 'Bitte genehmigen Sie mindestens eine E-Mail, bevor Sie die Kampagne starten';
	}
	return '';
};

const TABS = [
	{ key: 'overview', label: 'Übersicht' },
	{ key: 'recipients', label: 'Empfänger' },
	{ key: 'schedule', label: 'Zeitplan' },
	{ key: 'statistics', label: 'Statistik' },
	{ key: 'advanced', label: 'Erweiterte Einstellungen' },
];

export const MailCampaignsDetailsPageContent = () => {
	const navigate = useNavigate();
	const { id: campaignId } = useParams();
	const [campaignName, setCampaignName] = useState('');
	const [emailAccountId, setEmailAccountId] = useState('');
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [emailToDelete, setEmailToDelete] = useState(null);
	const [recipientToRemove, setRecipientToRemove] = useState(null);
	const [listToAttach, setListToAttach] = useState(null);
	const [isEditingLists, setIsEditingLists] = useState(false);

	const {
		campaign,
		loadingCampaign,
		handleSaveCampaign,
		contactLists: allContactLists = [],
		flattenedLeads = [],
		handleRemoveRecipientFromCampaign,
	} = useEmailCampaignDetailsContext();

	const isUpdatingCampaign = useIsMutating({ mutationKey: emailCampaignsMutationKeys.update }) > 0;
	const isUpsertingLists = useIsMutating({ mutationKey: emailCampaignsMutationKeys.upsertLists }) > 0;
	const savingCampaign = isUpdatingCampaign || isUpsertingLists;
	const removingRecipient = useIsMutating({ mutationKey: emailCampaignsMutationKeys.removeRecipient }) > 0;

	const { data: overviewStatsData, isLoading: loadingOverviewStats } = useCampaignStats(campaignId);
	const { isLoading: loadingRecentSends } = useCampaignSends(campaignId, { page: 1, pageSize: 3 });

	const { data: generationStatus } = useGenerationStatus(
		campaign?.campaignType === 'ai_personalized' ? campaignId : null,
	);
	const approvedEmailCount = generationStatus?.stats?.approved ?? 0;
	const { hasCapability: canSend } = useCapabilityCheck('email.send');

	const { data: emailAccounts = [], isLoading: isLoadingAccounts } = useAllEmailAccounts();
	const connectedAccounts = emailAccounts.filter((acc) => acc.connectionStatus === 'connected');

	const deleteEmailMutation = useDeleteSingleEmailOfCampaign();
	const upsertEmailListsMutation = useUpsertEmailListsOfCampaign();

	const { showSuccess, showError } = useError();

	const tabs =
		campaign?.campaignType === 'ai_personalized'
			? [...TABS.slice(0, 2), { key: 'ai-review', label: 'E-Mail Generierung' }, ...TABS.slice(2)]
			: TABS;
	const [activeTab, setActiveTab] = useUrlTab(
		tabs.map(({ key }) => key),
		'overview',
	);

	useEffect(() => {
		if (campaign?.campaignName) {
			setCampaignName(campaign.campaignName);
		}
		if (campaign?.emailAccountId) {
			setEmailAccountId(campaign.emailAccountId);
		}
	}, [campaign?.id, campaign?.campaignName, campaign?.emailAccountId]);

	if (loadingCampaign) {
		return (
			<Page>
				<div className="flex justify-center py-8">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
				</div>
			</Page>
		);
	}

	if (!campaign) {
		return (
			<Page>
				<p className="text-center py-8 text-warning-700">Kampagne nicht gefunden.</p>
			</Page>
		);
	}

	const steps = [...(campaign?.emailCampaignSteps || [])].sort((a, b) => a.stepOrder - b.stepOrder);
	const transformedEmails = steps.map((step) => ({
		id: step.id,
		stepOrder: step.stepOrder,
		subject: step.emailTemplates?.emailSubject || '',
		content: step.emailTemplates?.emailContent || '',
		delayDays: step.delayDays || 0,
		emailTemplateId: step.emailTemplateId,
		templateName: step.emailTemplates?.name || '',
	}));

	const selectedLists = campaign?.emailCampaign_emailList || [];
	const selectedListIds = selectedLists.map((list) => (list.contactLists ? list.contactLists.id : list.id));
	// Email campaigns only reach recipients WITH an email address, so the recipient total reflects
	// emailRecipientCount (not the full recipientCount, which also includes recipients without email).
	// The campaign's embedded contactLists only carry id + name, so the counts come from
	// allContactLists (the full list payload).
	const totalRecipients = selectedLists.reduce((sum, list) => {
		const fullListData = allContactLists.find((l) => l.id === list.contactLists?.id);
		return sum + (fullListData?.emailRecipientCount ?? 0);
	}, 0);

	const startBlockedReason = buildStartBlockedReason({
		canSend,
		campaignType: campaign?.campaignType,
		approvedEmailCount,
		emailAccountId,
		listCount: selectedLists.length,
		emailCount: transformedEmails.length,
	});

	const overviewStats = overviewStatsData?.stats ?? {};
	const emailStatistics = { sentCount: overviewStats.sent ?? 0 };

	const saveCampaignWithExtra = async (extra, successMsg) => {
		if (!handleSaveCampaign) return;
		try {
			await handleSaveCampaign(buildSavePayload({ campaignName, campaign, emailAccountId, extra }));
			if (successMsg) {
				showSuccess({ title: successMsg.title, message: successMsg.message, autoClose: true });
			}
		} catch (error) {
			showError({ title: successMsg?.errorTitle || 'Fehler beim Speichern', error, autoClose: true });
		}
	};

	const handleSaveCampaignData = () => saveCampaignWithExtra();

	const handleToggleCampaignStatus = async () => {
		if (!campaign || !handleSaveCampaign) return;
		const newStatus = campaign.campaignStatus === 'active' ? 'paused' : 'active';
		try {
			const payload = buildSavePayload({ campaignName, campaign, emailAccountId });
			payload.status = newStatus;
			await handleSaveCampaign(payload);
		} catch (error) {
			showError({ title: 'Fehler beim Ändern des Status', error, autoClose: true });
		}
	};

	const handleConfirmDelete = async () => {
		if (!emailToDelete) return;
		try {
			await deleteEmailMutation.mutateAsync({ campaignId, stepId: emailToDelete.id });
			setShowDeleteModal(false);
			setEmailToDelete(null);
		} catch (error) {
			showError({ title: 'Fehler beim Löschen', error, autoClose: true });
		}
	};

	const applyListToggle = async (listId) => {
		try {
			const newSelectedIds = selectedListIds.includes(listId)
				? selectedListIds.filter((id) => id !== listId)
				: [...selectedListIds, listId];
			await upsertEmailListsMutation.mutateAsync({ campaignId, listIds: newSelectedIds });
		} catch (error) {
			showError({ title: 'Fehler beim Ändern der Listen', error, autoClose: true });
		}
	};

	const handleListToggle = async (listId) => {
		const isAdding = !selectedListIds.includes(listId);
		const list = allContactLists.find((entry) => entry.id === listId);

		// Attaching a list to a running campaign schedules sends for everyone on it. Removing one only
		// cancels sends, and paused/draft campaigns schedule nothing — neither needs a confirmation.
		// A list with no email recipients triggers no send either, so it stays silent. An unknown
		// count (list missing from the payload) is not the same as zero and does warn.
		const sendsNothing = list?.emailRecipientCount === 0;
		if (isAdding && campaign.campaignStatus === 'active' && !sendsNothing) {
			setListToAttach(list ?? { id: listId });
			return;
		}

		await applyListToggle(listId);
	};

	const handleConfirmListAttach = async () => {
		const listId = listToAttach?.id;
		setListToAttach(null);
		if (listId) await applyListToggle(listId);
	};

	const handleScheduleChange = (schedule) =>
		saveCampaignWithExtra(
			{ sendingSchedule: schedule },
			{
				title: 'Zeitplan gespeichert',
				message: 'Der Versandzeitplan wurde erfolgreich gespeichert.',
				errorTitle: 'Fehler beim Speichern',
			},
		);

	const handleAdvancedSettingsChange = (settings) =>
		saveCampaignWithExtra(
			{ maxEmailsPerDay: settings.maxEmailsPerDay, showUnsubscribeLink: settings.showUnsubscribeLink },
			{
				title: 'Einstellungen gespeichert',
				message: 'Die erweiterten Einstellungen wurden erfolgreich gespeichert.',
				errorTitle: 'Fehler beim Speichern',
			},
		);

	const handleSignatureChange = (signatureId) =>
		saveCampaignWithExtra(
			{ signatureId },
			{
				title: 'Signatur gespeichert',
				message: 'Die E-Mail-Signatur wurde erfolgreich aktualisiert.',
				errorTitle: 'Fehler beim Speichern',
			},
		);

	return (
		<Page
			header={
				<PageHeader
					breadcrumbs={[{ label: 'E-Mail Kampagnen', to: '/email-campaigns' }, campaignName || 'Kampagne']}
				>
					<StatusBadge status={campaign.campaignStatus} />
					<div className="w-px h-5 bg-bgBase-500 mx-0.5" />
					{campaign.campaignStatus === 'active' ? (
						<Button onClick={handleToggleCampaignStatus} variant="outline" size="sm">
							<BsPauseFill className="w-3 h-3" /> Pausieren
						</Button>
					) : (
						<Button
							onClick={handleToggleCampaignStatus}
							disabled={Boolean(startBlockedReason)}
							title={startBlockedReason}
							variant="outline"
							size="sm"
						>
							<BsPlayFill className="w-3 h-3" /> Starten
						</Button>
					)}
					<Button onClick={handleSaveCampaignData} variant="outline" size="sm" loading={savingCampaign}>
						{savingCampaign ? 'Speichert…' : 'Speichern'}
					</Button>
				</PageHeader>
			}
		>
			<div>
				<div className="mb-6">
					<CampaignStatsCards
						statistics={emailStatistics}
						loading={loadingOverviewStats || loadingRecentSends}
						emailCount={transformedEmails.length}
						recipientCount={totalRecipients}
						campaignType={campaign.campaignType}
					/>
				</div>

				<Tabs
					tabs={tabs.map(({ key, label }) => ({ id: key, label }))}
					activeId={activeTab}
					onChange={setActiveTab}
					className="mb-5"
				/>

				{activeTab === 'overview' && (
					<OverviewTab
						campaign={campaign}
						campaignId={campaignId}
						campaignName={campaignName}
						setCampaignName={setCampaignName}
						emailAccountId={emailAccountId}
						setEmailAccountId={setEmailAccountId}
						isLoadingAccounts={isLoadingAccounts}
						connectedAccounts={connectedAccounts}
						transformedEmails={transformedEmails}
						onDeleteEmail={(email) => {
							setEmailToDelete(email);
							setShowDeleteModal(true);
						}}
						selectedLists={selectedLists}
						selectedListIds={selectedListIds}
						allContactLists={allContactLists}
						totalRecipients={totalRecipients}
						isEditingLists={isEditingLists}
						setIsEditingLists={setIsEditingLists}
						onListToggle={handleListToggle}
						onNavigate={navigate}
					/>
				)}

				{activeTab === 'ai-review' && campaign.campaignType === 'ai_personalized' && (
					<AICampaignReviewTab campaignId={campaignId} />
				)}

				{activeTab === 'recipients' && (
					<RecipientVariablesTable
						leads={flattenedLeads}
						onRemoveRecipient={(lead) => setRecipientToRemove(lead)}
						removingRecipientId={removingRecipient ? recipientToRemove?.id : null}
					/>
				)}

				{activeTab === 'schedule' && (
					<ScheduleTab sendingSchedule={campaign.sendingSchedule} onScheduleChange={handleScheduleChange} />
				)}

				{activeTab === 'advanced' && (
					<AdvancedSettingsTab
						maxEmailsPerDay={campaign.maxEmailsPerDay}
						signatureId={campaign.signatureId}
						showUnsubscribeLink={campaign.showUnsubscribeLink}
						onSettingsChange={handleAdvancedSettingsChange}
						onSignatureChange={handleSignatureChange}
					/>
				)}

				{activeTab === 'statistics' && (
					<StatisticsTab
						campaignId={campaignId}
						campaignName={campaignName}
						campaignStatus={campaign.campaignStatus}
					/>
				)}
			</div>

			<ConfirmModal
				isOpen={showDeleteModal}
				onClose={() => {
					setShowDeleteModal(false);
					setEmailToDelete(null);
				}}
				onConfirm={handleConfirmDelete}
				title="E-Mail löschen"
				message={`Möchten Sie die E-Mail "${emailToDelete?.subject || 'ohne Betreff'}" wirklich aus dieser Kampagne löschen?`}
				confirmText="E-Mail löschen"
				cancelText="Abbrechen"
				isLoading={deleteEmailMutation.isPending}
			/>

			<ConfirmModal
				isOpen={Boolean(recipientToRemove)}
				onClose={() => setRecipientToRemove(null)}
				onConfirm={() => {
					handleRemoveRecipientFromCampaign(recipientToRemove.id);
				}}
				title="Empfänger entfernen"
				message={
					<div>
						<p className="mb-3">
							<span className="font-semibold">
								&quot;{recipientToRemove?.fullname || recipientToRemove?.emailAddress}&quot;
							</span>{' '}
							wird aus dieser Kampagne entfernt.
						</p>
						<p className="text-sm font-medium text-text mb-2">Folgendes passiert:</p>
						<ul className="list-disc list-inside text-sm text-text space-y-1">
							<li>Der Kontakt wird aus allen Kontaktlisten dieser Kampagne entfernt</li>
							<li>Alle geplanten (noch nicht versendeten) E-Mails werden storniert</li>
							<li>Bereits versendete E-Mails sind nicht betroffen</li>
						</ul>
					</div>
				}
				confirmText="Entfernen"
				cancelText="Abbrechen"
				isLoading={removingRecipient}
			/>

			<ConfirmModal
				isOpen={Boolean(listToAttach)}
				onClose={() => setListToAttach(null)}
				onConfirm={handleConfirmListAttach}
				title="Liste zu laufender Kampagne hinzufügen"
				message={
					<div>
						<p className="mb-3">
							Die Kampagne <span className="font-semibold">&quot;{campaignName}&quot;</span> läuft gerade.
							Die Liste{' '}
							<span className="font-semibold">&quot;{listToAttach?.name ?? 'diese Liste'}&quot;</span>{' '}
							wird ihr als Empfängerquelle hinzugefügt.
						</p>
						<p className="text-sm font-medium text-text mb-2">Folgendes passiert:</p>
						<ul className="list-disc list-inside text-sm text-text space-y-1">
							<li>
								{describeListRecipients(listToAttach?.emailRecipientCount)} automatisch die E-Mails der
								Kampagne
							</li>
							<li>Der Versand startet, sobald das Sendefenster der Kampagne es zulässt</li>
							<li>
								Empfänger, die bereits über eine andere Liste in der Kampagne sind, bleiben unberührt
							</li>
						</ul>
					</div>
				}
				subtitle={null}
				confirmText="Liste hinzufügen"
				cancelText="Abbrechen"
			/>
		</Page>
	);
};

const MailCampaignsDetailsPage = () => {
	const { id } = useParams();

	return (
		<EmailCampaignDetailsProvider campaignId={id}>
			<MailCampaignsDetailsPageContent />
		</EmailCampaignDetailsProvider>
	);
};

export default MailCampaignsDetailsPage;
